import type { ClientRelease } from '../../../shared/types/collection'
import {
	getCollectionMasterYears,
	queueCollectionEnrichment,
} from '../../api/collection'
import {
	applyCollectionMasterYearUpdates,
	refreshCollectionStatus,
} from '../../stores/collectionStore'
import type { CollectionPageContext } from './collectionPageData'

type CollectionMasterEnrichmentOptions = {
	isCurrentPageContext: (context: CollectionPageContext) => boolean
	setMasterYearSortAvailability: (available: boolean) => void
}

/** Keeps enrichment work with the cached view and polls only while it is visible. */
export class CollectionMasterEnrichment {
	private readonly pages = new Map<
		string,
		{ context: CollectionPageContext; ids: number[]; queued: boolean }
	>()
	private timer?: ReturnType<typeof setTimeout>
	private active = false
	private complete = false
	private coverageDeadline?: number | null
	private generation = 0
	private disposed = false

	constructor(private readonly options: CollectionMasterEnrichmentOptions) {}

	async queueVisible(
		releases: ClientRelease[],
		context: CollectionPageContext,
	): Promise<void> {
		if (context.offset === 0) this.pages.clear()
		this.pages.set(context.cacheKey, {
			context,
			ids: releases.map((release) => release.id),
			queued: false,
		})
		if (this.active) await this.queuePending()
	}

	updateCoveragePolling(complete: boolean, validUntil?: string | null): void {
		this.complete = complete
		this.coverageDeadline =
			typeof validUntil === 'string' ? Date.parse(validUntil) : validUntil
		clearTimeout(this.timer)
		this.timer = undefined
		this.schedule()
	}

	pause(): void {
		this.active = false
		this.generation++
		clearTimeout(this.timer)
		this.timer = undefined
	}

	resume(): void {
		if (this.disposed || this.active) return
		this.active = true
		void this.queuePending()
		this.schedule()
	}

	destroy(): void {
		this.disposed = true
		this.pause()
		this.pages.clear()
	}

	private async queuePending(): Promise<void> {
		const generation = this.generation
		for (const page of this.pages.values()) {
			if (!this.active || generation !== this.generation) return
			if (page.queued || !this.options.isCurrentPageContext(page.context))
				continue
			page.queued = true
			try {
				await queueCollectionEnrichment(page.ids)
			} catch {
				page.queued = false
			}
		}
		this.schedule()
	}

	private schedule(): void {
		if (!this.active || this.timer || this.disposed) return
		if (this.complete && this.coverageDeadline === null) return
		let delay = 2500
		if (this.complete) {
			// Older cached pages lack a deadline; failed expiry reads also use this delay.
			delay = 60_000
			if (
				typeof this.coverageDeadline === 'number' &&
				Number.isFinite(this.coverageDeadline)
			) {
				delay = Math.max(2500, this.coverageDeadline - Date.now())
			}
		}
		this.timer = setTimeout(
			() => {
				this.timer = undefined
				void this.poll()
			},
			Math.min(delay, 2_147_483_647),
		)
	}

	private async poll(): Promise<void> {
		const generation = this.generation
		const wasComplete = this.complete
		try {
			const pages = [...this.pages.values()].filter((page) =>
				this.options.isCurrentPageContext(page.context),
			)
			const ids = [...new Set(pages.flatMap((page) => page.ids))]
			const [status, updates] = await Promise.all([
				refreshCollectionStatus(),
				ids.length
					? getCollectionMasterYears(ids)
					: Promise.resolve([]),
			])
			if (!this.active || generation !== this.generation || this.disposed)
				return
			const currentIds = new Set(
				pages
					.filter((page) =>
						this.options.isCurrentPageContext(page.context),
					)
					.flatMap((page) => page.ids),
			)
			applyCollectionMasterYearUpdates(
				updates.filter((update) => currentIds.has(update.instanceId)),
			)
			this.complete = !!status.masterCoverage?.complete
			const validUntil = status.masterCoverage?.validUntil
			this.coverageDeadline =
				typeof validUntil === 'string'
					? Date.parse(validUntil)
					: validUntil
			this.options.setMasterYearSortAvailability(this.complete)
			if (!this.complete && status.usable !== false) {
				if (wasComplete)
					for (const page of this.pages.values()) page.queued = false
				await this.queuePending()
			}
		} catch (error) {
			if (generation === this.generation)
				this.coverageDeadline = undefined
			if (this.active && generation === this.generation)
				console.warn('Could not update collection enrichment', error)
		} finally {
			if (generation === this.generation) this.schedule()
		}
	}
}
